import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ProtectedRoute from "@/components/ProtectedRoute";
import { INSTITUTION_ROLES } from "@/contexts/AuthContext";
import { CbtSubscriptionGate } from "@/components/CbtSubscriptionGate";

// Lazy-loaded routes
const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const FreeTest = lazy(() => import("./pages/FreeTest"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const ExamSimulation = lazy(() => import("./pages/ExamSimulation"));
const InternationalExam = lazy(() => import("./pages/InternationalExam"));
const PastQuestions = lazy(() => import("./pages/PastQuestions"));
const PracticeExam = lazy(() => import("./pages/PracticeExam"));
const Solutions = lazy(() => import("./pages/Solutions"));
const InstitutionDashboard = lazy(() => import("./pages/InstitutionDashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Competitions = lazy(() => import("./pages/Competitions"));
const SpeakingChallenge = lazy(() => import("./pages/SpeakingChallenge"));
const CbtChampionship = lazy(() => import("./pages/CbtChampionship"));
const CompetitionRegister = lazy(() => import("./pages/CompetitionRegister"));
const CompetitionDashboard = lazy(() => import("./pages/CompetitionDashboard"));
const CompetitionExam = lazy(() => import("./pages/CompetitionExam"));
const CompetitionResults = lazy(() => import("./pages/CompetitionResults"));
const Sponsors = lazy(() => import("./pages/Sponsors"));
const MyInstitutionExams = lazy(() => import("./pages/MyInstitutionExams"));
const InstitutionExamRun = lazy(() => import("./pages/InstitutionExamRun"));
const PreparationSetup = lazy(() => import("./pages/PreparationSetup"));
const PackageBuy = lazy(() => import("./pages/PackageBuy"));
const PaymentCallback = lazy(() => import("./pages/PaymentCallback"));
const MyPurchases = lazy(() => import("./pages/MyPurchases"));
const LearningPacks = lazy(() => import("./pages/LearningPacks"));
const CbtPractice = lazy(() => import("./pages/CbtPractice"));

const Cart = lazy(() => import("./pages/Cart"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Trust = lazy(() => import("./pages/Trust"));
const RegistrationSlip = lazy(() => import("./pages/RegistrationSlip"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,    // 5 min stale time for caching
      gcTime: 10 * 60 * 1000,       // 10 min garbage collection
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/free-test" element={<FreeTest />} />
                <Route path="/competitions" element={<Competitions />} />
                <Route path="/competitions/speaking" element={<SpeakingChallenge />} />
                <Route path="/competitions/cbt" element={<CbtChampionship />} />
                <Route path="/competitions/cbt/register" element={<ErrorBoundary><CompetitionRegister /></ErrorBoundary>} />
                <Route path="/competitions/slip/:token" element={<ErrorBoundary><RegistrationSlip /></ErrorBoundary>} />
                <Route path="/competitions/cbt/dashboard" element={<ProtectedRoute requireRole="student"><ErrorBoundary><CompetitionDashboard /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/competitions/cbt/exam" element={<ProtectedRoute requireRole="student"><ErrorBoundary><CompetitionExam /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/competitions/cbt/results" element={<ProtectedRoute requireRole="student"><ErrorBoundary><CompetitionResults /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/sponsors" element={<Sponsors />} />
                <Route path="/trust" element={<Trust />} />
                <Route path="/dashboard" element={<ProtectedRoute requireRoles={["student", "admin"]}><ErrorBoundary><Dashboard /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute requireAdmin><ErrorBoundary><AdminPanel /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/exam" element={<ProtectedRoute requireRole="student"><ErrorBoundary><CbtSubscriptionGate><ExamSimulation /></CbtSubscriptionGate></ErrorBoundary></ProtectedRoute>} />
                <Route path="/international-exam/:examId" element={<ProtectedRoute requireRole="student"><ErrorBoundary><InternationalExam /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/past-questions" element={<ProtectedRoute requireRole="student"><ErrorBoundary><PastQuestions /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/practice" element={<ProtectedRoute requireRole="student"><ErrorBoundary><CbtSubscriptionGate><PracticeExam /></CbtSubscriptionGate></ErrorBoundary></ProtectedRoute>} />
                <Route path="/solutions" element={<ProtectedRoute requireRole="student"><ErrorBoundary><Solutions /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/onboarding" element={<ProtectedRoute requireRole="student" skipOnboarding><ErrorBoundary><Onboarding /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/institution-dashboard" element={<ProtectedRoute requireRoles={INSTITUTION_ROLES}><ErrorBoundary><InstitutionDashboard /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/my-institution-exams" element={<ProtectedRoute requireRole="student"><ErrorBoundary><MyInstitutionExams /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/institution-exam/:examId" element={<ProtectedRoute requireRole="student"><ErrorBoundary><InstitutionExamRun /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/preparation/setup" element={<ProtectedRoute requireRole="student"><ErrorBoundary><PreparationSetup /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/buy/:id" element={<ProtectedRoute requireRole="student"><ErrorBoundary><PackageBuy /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/buy/:kind/:id" element={<ProtectedRoute requireRole="student"><ErrorBoundary><PackageBuy /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/payment/callback" element={<ProtectedRoute requireRole="student"><ErrorBoundary><PaymentCallback /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/my-purchases" element={<ProtectedRoute requireRole="student"><ErrorBoundary><MyPurchases /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/learning-packs" element={<ProtectedRoute requireRole="student"><ErrorBoundary><LearningPacks /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/cbt-practice" element={<ProtectedRoute requireRole="student"><ErrorBoundary><CbtPractice /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/mock-exams" element={<Navigate to="/exam" replace />} />
                <Route path="/cart" element={<ProtectedRoute requireRole="student"><ErrorBoundary><Cart /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute requireRole="student"><ErrorBoundary><Settings /></ErrorBoundary></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
